// Tutorial step configuration for MVP (steps 1-3)
import type { GameState } from '../types/game'
import {
  TUTORIAL_INITIAL_STATE,
  TUTORIAL_CARD_PLAYED_STATE,
  TUTORIAL_AUCTION_RESULT_STATE,
  TUTORIAL_ROUND_ENDING_STATE,
  TUTORIAL_VALUATION_STATE,
  TUTORIAL_ROUND_COMPLETE_STATE,
  TUTORIAL_ROUND_2_PLAYING,
  TUTORIAL_ROUND_2_VALUATION,
  TUTORIAL_ROUND_2_COMPLETE,
  TUTORIAL_ROUND_3_VALUATION,
  TUTORIAL_ROUND_3_COMPLETE,
  TUTORIAL_ROUND_4_VALUATION,
  TUTORIAL_GAME_COMPLETE
} from './tutorialScenario'

export type TutorialPhase =
  | 'intro'
  | 'card_selection'
  | 'auction_basics'
  | 'auction_types'
  | 'round_ending'
  | 'valuation'
  | 'scoring'
  | 'complete'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'center'

export type HighlightType = 'glow' | 'mask' | 'pulse'

export type TooltipContent = string | {
  html: string  // Formatted HTML content
}

export interface TooltipConfig {
  title?: string
  content: TooltipContent
  position: TooltipPosition
  targetElement: string | null
  showControls: boolean
}

export interface HighlightTarget {
  elementId: string
  type: HighlightType
  label?: string
}

export interface TutorialAction {
  type: 'card_play' | 'bid' | 'pass' | 'buy' | 'set_price' | 'round_end' | 'valuation'
  actor: string
  description: string
  cardId?: string
  bidAmount?: number
  targetPlayerId?: string
}

export interface TutorialStep {
  id: number
  phase: TutorialPhase
  gameState: GameState
  action?: TutorialAction
  tooltip: TooltipConfig
  highlights: HighlightTarget[]
  waitForUserAction?: boolean
  autoAdvanceDelay?: number
  backdropOpacity?: number
  selectedCardId?: string | null
}

// ============================================================================
// MVP: Steps 1-3 (Introductory - informational only)
// ============================================================================

export const TUTORIAL_STEPS: TutorialStep[] = [
  // ==========================================
  // STEP 1: Welcome Screen
  // ==========================================
  {
    id: 1,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Welcome to Bougie Art!',
      content: "You are an art dealer buying and selling paintings. The player with the most money after 4 rounds wins. Let's learn how to play!",
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 2: Artist Board Introduction
  // ==========================================
  {
    id: 2,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'The Artists',
      content: "These are the 5 artists whose work you'll trade. Track how many cards are played for each artist - the most popular ones become valuable at round end!",
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 3: Player Hand Introduction
  // ==========================================
  {
    id: 3,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Your Cards',
      content: "These are your artwork cards. Each shows an artist name and an auction type symbol. On your turn, you'll play one card to start an auction.",
      position: 'top',
      targetElement: 'player-card-0', // Position tooltip relative to first card
      showControls: true,
    },
    highlights: [
      // Highlight all 6 cards with mask (cutout effect to keep them in focus)
      { elementId: 'player-card-0', type: 'mask' },
      { elementId: 'player-card-1', type: 'mask' },
      { elementId: 'player-card-2', type: 'mask' },
      { elementId: 'player-card-3', type: 'mask' },
      { elementId: 'player-card-4', type: 'mask' },
      { elementId: 'player-card-5', type: 'mask' },
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 4: Round Indicator
  // ==========================================
  {
    id: 4,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Round Tracker',
      content: "The game lasts 4 rounds. After each round, the most popular artists become valuable and their paintings are sold to the bank. Try to end each round with valuable artists!",
      position: 'bottom',
      targetElement: 'round-indicator',
      showControls: true,
    },
    highlights: [
      { elementId: 'round-indicator', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 5: Opponents Panel
  // ==========================================
  {
    id: 5,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Your Opponents',
      content: "These are the other players. Each has their own money and strategy. Watch how they bid - you can learn from their patterns and use it to your advantage!",
      position: 'left',
      targetElement: 'opponents-panel',
      showControls: true,
    },
    highlights: [
      { elementId: 'opponents-panel', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 6: Auction Area
  // ==========================================
  {
    id: 6,
    phase: 'intro',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Auction Area',
      content: "This is where auctions happen! When someone plays a card, the artwork appears here and players take turns bidding. The highest bidder wins the painting.",
      position: 'left',
      targetElement: 'auction-center-null',
      showControls: true,
    },
    highlights: [
      { elementId: 'auction-center-null', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 7: Your First Auction
  // ==========================================
  {
    id: 7,
    phase: 'card_selection',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: 'Time to Play!',
      content: "It's your turn! The first card in your hand (a Solstice Vane) is selected. Notice the 'Play Card' button appears in the auction area. Click Next to see what happens when you play it!",
      position: 'top',
      targetElement: 'player-card-0',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-0', type: 'mask' },
      { elementId: 'auction-center-null', type: 'mask' }
    ],
    backdropOpacity: 0.75,
    selectedCardId: 'tutorial-card-0',
  },

  // ==========================================
  // STEP 8: Card in Play
  // ==========================================
  {
    id: 8,
    phase: 'auction_basics',
    gameState: TUTORIAL_CARD_PLAYED_STATE,
    tooltip: {
      title: 'Underground Auction Begins',
      content: "Your card is now in the auction area! In an UNDERGROUND auction, everyone secretly writes a bid. The highest bidder wins and pays their bid amount. Let's watch the bidding happen...",
      position: 'bottom',
      targetElement: 'auction-hidden',
      showControls: true,
    },
    highlights: [
      { elementId: 'auction-hidden', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 9: You Won!
  // ==========================================
  {
    id: 9,
    phase: 'auction_basics',
    gameState: TUTORIAL_AUCTION_RESULT_STATE,
    tooltip: {
      title: 'You Won the Auction!',
      content: "You won with a bid of $8k! The painting is now in Your Purchases (left panel), and your money decreased from $100k to $92k. Alice and Bob didn't bid high enough.",
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 10: The 5 Auction Types
  // ==========================================
  {
    id: 10,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '5 Auction Types - Overview',
      content: "Each card shows its auction type symbol. From left to right: UNDERGROUND (🙈), INSIDE JOB (🤝), FLEX (💪), PRICE FIXING (💰), and TWOFER (👯). Click Next to learn about each type!",
      position: 'top',
      targetElement: 'player-card-0',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-0', type: 'mask' },
      { elementId: 'player-card-1', type: 'mask' },
      { elementId: 'player-card-2', type: 'mask' },
      { elementId: 'player-card-3', type: 'mask' },
      { elementId: 'player-card-4', type: 'mask' },
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 10A: Hidden Auction Details
  // ==========================================
  {
    id: 11,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '🙈 UNDERGROUND',
      content: "Everyone secretly writes ONE bid at the same time. Highest bid wins and pays their bid amount. Great for bluffing! If everyone bids $0, auctioneer gets it free. Ties go to player closest to auctioneer clockwise.",
      position: 'top',
      targetElement: 'player-card-0',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-0', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 10B: One Offer Auction Details
  // ==========================================
  {
    id: 12,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '🤝 INSIDE JOB',
      content: "Starting left of auctioneer and going clockwise, each player gets exactly ONE chance to bid. Must bid higher than current bid or pass. Auctioneer bids last. If everyone passes, auctioneer gets it free!",
      position: 'top',
      targetElement: 'player-card-1',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-1', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 10C: Open Auction Details
  // ==========================================
  {
    id: 13,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '💪 FLEX',
      content: "Free-for-all bidding! Everyone (including auctioneer) can bid any number of times, in any order. Just keep bidding higher! Ends when no one wants to bid higher. If no one bids, auctioneer gets it FREE.",
      position: 'top',
      targetElement: 'player-card-2',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-2', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 10D: Fixed Price Details
  // ==========================================
  {
    id: 14,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '💰 PRICE FIXING',
      content: "Auctioneer announces a price. Going clockwise, first player to take it buys at that price and auction ends immediately. If everyone passes, auctioneer MUST buy it themselves! Can't set price higher than what you can afford.",
      position: 'top',
      targetElement: 'player-card-3',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-3', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 10E: Double Auction Details
  // ==========================================
  {
    id: 15,
    phase: 'auction_types',
    gameState: TUTORIAL_INITIAL_STATE,
    tooltip: {
      title: '👯 TWOFER',
      content: "Auctioneer may offer a SECOND painting of the same artist. Both auctioned together using the second card's auction type. If no second card, others can play one (become new auctioneer)! WARNING: If 5th card is Double + second, BOTH paintings are unsold!",
      position: 'top',
      targetElement: 'player-card-4',
      showControls: true,
    },
    highlights: [
      { elementId: 'player-card-4', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 16: How Rounds End
  // ==========================================
  {
    id: 16,
    phase: 'round_ending',
    gameState: TUTORIAL_ROUND_ENDING_STATE,
    tooltip: {
      title: '📋 Rounds End with the 5th Card',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">When the <strong>5th painting</strong> of ANY artist is played, the round ends immediately!</p>
          <ul style="margin: 0; padding-left: 20px; color: rgba(255,255,255,0.85);">
            <li>That 5th painting is <strong>NOT auctioned</strong></li>
            <li>We count how many paintings <strong>SOLD</strong> for each artist</li>
            <li>Top 3 sellers get value tiles</li>
          </ul>
          <p style="margin: 0; color: #fbbf24;">Only sold paintings count toward values!</p>
        </div>`
      },
      position: 'right',
      targetElement: 'round-indicator',
      showControls: true,
    },
    highlights: [
      { elementId: 'round-indicator', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 17: Artist Valuation (Round 1)
  // ==========================================
  {
    id: 17,
    phase: 'valuation',
    gameState: TUTORIAL_VALUATION_STATE,
    tooltip: {
      title: '💰 Setting Artist Values - Round 1',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">Only artists with <strong>SOLD paintings</strong> get value!</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
            <div style="margin-bottom: 8px;"><strong>Round 1 Results:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥇 1st: Solstice Vane</span>
              <span style="color: #fbbf24;">+$30k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥈 2nd: Mira Faux</span>
              <span style="color: #fbbf24;">+$20k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥉 3rd: None</span>
              <span style="color: rgba(255,255,255,0.5);">+$0</span>
            </div>
          </div>
        </div>`
      },
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 18: Round 1 Complete - Get Paid!
  // ==========================================
  {
    id: 18,
    phase: 'scoring',
    gameState: TUTORIAL_ROUND_COMPLETE_STATE,
    tooltip: {
      title: '✅ Round 1 Complete - Get Paid!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">All your paintings are <strong>sold to the bank</strong> at their Round 1 values!</p>
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #4CAF50;">
            <div style="margin-bottom: 8px;"><strong>Your Payout:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>2 × Solstice Vane ($30k each)</span>
              <span style="color: #4CAF50;">+$60k</span>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-top: 1px solid rgba(255,255,255,0.1);">
            <span>Your money: $70k + $60k</span>
            <span style="color: #fbbf24; font-weight: 600;">= $130k</span>
          </div>
          <p style="margin: 0; color: #4CAF50;">✓ Round 2 begins! Values are set for next round.</p>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 19: Round 2 Begins - Values Persist!
  // ==========================================
  {
    id: 19,
    phase: 'valuation',
    gameState: TUTORIAL_ROUND_2_PLAYING,
    tooltip: {
      title: '🔄 Round 2 Begins - Values Persist!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">Look at the Artist Board - the values from Round 1 are <strong>still there</strong>!</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px;">
            <div style="margin-bottom: 8px;"><strong>Current Values (from Round 1):</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>Solstice Vane (1st place)</span>
              <span style="color: #fbbf24;">$30k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Mira Faux (2nd place)</span>
              <span style="color: #fbbf24;">$20k</span>
            </div>
          </div>
          <p style="margin: 0;">As paintings sell this round, <strong>NEW values will be ADDED</strong> to these!</p>
          <p style="margin: 0; color: #fbbf24;">You have $130k and 2 cards left. Let's see how Round 2 plays out...</p>
        </div>`
      },
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 20: Round 2 Valuation - Values Accumulate!
  // ==========================================
  {
    id: 20,
    phase: 'valuation',
    gameState: TUTORIAL_ROUND_2_VALUATION,
    tooltip: {
      title: '📈 Value Accumulation in Action!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">Look at the Artist Board - values have <strong>accumulated</strong>!</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
            <div style="margin-bottom: 8px;"><strong>Round 2 Results:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥇 Mira Faux (3 sold)</span>
              <span style="color: #fbbf24;">+$30k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥈 Solstice Vane (2 sold)</span>
              <span style="color: #fbbf24;">+$20k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥉 Atlas Zero (2 sold)</span>
              <span style="color: #fbbf24;">+$10k</span>
            </div>
          </div>
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px;">
            <div style="margin-bottom: 8px;"><strong>New Total Values:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>Manuel: $30 + $20</span>
              <span style="color: #4CAF50;">= $50k each</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Sigrid: $20 + $30</span>
              <span style="color: #4CAF50;">= $50k each</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Daniel: $0 + $10</span>
              <span style="color: #4CAF50;">= $10k each</span>
            </div>
          </div>
        </div>`
      },
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 21: Round 2 Complete - Round 3 Begins
  // ==========================================
  {
    id: 21,
    phase: 'scoring',
    gameState: TUTORIAL_ROUND_2_COMPLETE,
    tooltip: {
      title: '💵 Round 2 Complete - Get Paid Again!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">Your paintings are sold at the <strong>accumulated values</strong>!</p>
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #4CAF50;">
            <div style="margin-bottom: 8px;"><strong>Your Payout:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>Atlas Zero ($10k each)</span>
              <span style="color: #4CAF50;">+$10k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Vesper Kint ($0k each)</span>
              <span style="color: rgba(255,255,255,0.5);">+$0</span>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-top: 1px solid rgba(255,255,255,0.1);">
            <span>Your total money</span>
            <span style="color: #fbbf24; font-weight: 600;">= $138k</span>
          </div>
          <p style="margin: 0; color: #4CAF50;">✓ You're dealt a new card (Rafael) for Round 3!</p>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 22: Round 3 - Artists Can Miss Rounds!
  // ==========================================
  {
    id: 22,
    phase: 'valuation',
    gameState: TUTORIAL_ROUND_3_VALUATION,
    tooltip: {
      title: '⚠️ Round 3 - What Happens When Artists Miss?',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">This round, <strong>THREE artists placed</strong>, but Daniel and Ramon <strong>missed the top 3</strong>!</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
            <div style="margin-bottom: 8px;"><strong>Round 3 Results:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥇 Solstice Vane (3 sold)</span>
              <span style="color: #fbbf24;">+$30k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥈 Mira Faux (2 sold)</span>
              <span style="color: #fbbf24;">+$20k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥉 Luxe Nebula (1 sold)</span>
              <span style="color: #fbbf24;">+$10k</span>
            </div>
          </div>
          <div style="background: rgba(255, 152, 0, 0.2); padding: 12px; border-radius: 8px; border-left: 3px solid #ff9800;">
            <div style="margin-bottom: 8px;"><strong>🚨 Key Rule - Missed Rounds:</strong></div>
            <p style="margin: 0 0 8px 0;">When an artist <strong>misses the top 3</strong> in a round:</p>
            <ul style="margin: 0; padding-left: 20px; color: rgba(255,255,255,0.9);">
              <li>Cards you bought <strong>THAT ROUND</strong> are worth <strong>$0</strong></li>
              <li>Cards from <strong>previous rounds</strong> keep their value</li>
              <li>Cards from <strong>future rounds</strong> get new value</li>
            </ul>
          </div>
          <p style="margin: 0; color: #fbbf24;">Daniel's R2 card = $10k (he placed in R2). But Daniel's R3 cards = $0 (he missed R3)!</p>
        </div>`
      },
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 23: Round 3 Complete - Round 4 Begins
  // ==========================================
  {
    id: 23,
    phase: 'scoring',
    gameState: TUTORIAL_ROUND_3_COMPLETE,
    tooltip: {
      title: '💰 Round 3 Complete - One More to Go!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #4CAF50;">
            <div style="margin-bottom: 8px;"><strong>Your Payout:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>Luxe Nebula ($10k)</span>
              <span style="color: #4CAF50;">+$10k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Atlas Zero ($10k)</span>
              <span style="color: #4CAF50;">+$10k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>+ Other paintings</span>
              <span style="color: #4CAF50;">+$50k</span>
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-top: 1px solid rgba(255,255,255,0.1);">
            <span>Your total money</span>
            <span style="color: #fbbf24; font-weight: 600;">= $188k</span>
          </div>
          <p style="margin: 0; color: #fbbf24;">⭐ <strong>Round 4</strong> - The final round! Last chance to buy!</p>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [
      { elementId: 'round-indicator', type: 'mask' }
    ],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 24: Round 4 Valuation - Final Values Set!
  // ==========================================
  {
    id: 24,
    phase: 'valuation',
    gameState: TUTORIAL_ROUND_4_VALUATION,
    tooltip: {
      title: '🏁 Round 4 Valuation - Final Values!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">The <strong>final value tiles</strong> are being set! This is the last valuation of the game.</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #fbbf24;">
            <div style="margin-bottom: 8px;"><strong>Round 4 Results:</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥇 Mira Faux (2 sold)</span>
              <span style="color: #fbbf24;">+$30k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥈 Solstice Vane (2 sold)</span>
              <span style="color: #fbbf24;">+$20k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>🥉 Luxe Nebula (1 sold)</span>
              <span style="color: #fbbf24;">+$10k</span>
            </div>
          </div>
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px;">
            <div style="margin-bottom: 8px;"><strong>Final Artist Values (all 4 rounds):</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span>Manuel: $30+$20+$30+$20</span>
              <span style="color: #4CAF50;">= $100k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Sigrid: $20+$30+$20+$30</span>
              <span style="color: #4CAF50;">= $100k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Rafael: $0+$0+$10+$10</span>
              <span style="color: #4CAF50;">= $20k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span>Daniel: $0+$10+$0+$0</span>
              <span style="color: #4CAF50;">= $10k</span>
            </div>
          </div>
        </div>`
      },
      position: 'right',
      targetElement: 'artist-board',
      showControls: true,
    },
    highlights: [
      { elementId: 'artist-board', type: 'mask' }
    ],
    backdropOpacity: 0.75,
  },

  // ==========================================
  // STEP 25: Game Over - You Won!
  // ==========================================
  {
    id: 25,
    phase: 'scoring',
    gameState: TUTORIAL_GAME_COMPLETE,
    tooltip: {
      title: '🎉 Game Over - You Won!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <div style="background: rgba(76, 175, 80, 0.15); padding: 12px; border-radius: 8px; border-left: 3px solid #4CAF50;">
            <div style="margin-bottom: 8px;"><strong>Final Payout:</strong></div>
            <p style="margin: 0;">All remaining paintings sold at <strong>final accumulated values</strong>!</p>
          </div>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px;">
            <div style="margin-bottom: 8px;"><strong>Final Standings (most money wins!):</strong></div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #fbbf24;">🥇 You</span>
              <span style="font-weight: 700;">$320k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: rgba(255,255,255,0.7);">🥈 Alice</span>
              <span>$245k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: rgba(255,255,255,0.7);">🥉 Bob</span>
              <span>$220k</span>
            </div>
          </div>
          <p style="margin: 0; text-align: center; color: #4CAF50; font-size: 16px; font-weight: 600;">
            🏆 YOU WIN! 🏆
          </p>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 26: Summary - How You Won
  // ==========================================
  {
    id: 26,
    phase: 'complete',
    gameState: TUTORIAL_GAME_COMPLETE,
    tooltip: {
      title: '📊 How You Won',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">You won by buying paintings from artists who <strong>placed in multiple rounds</strong> - their values accumulated!</p>
          <div style="background: rgba(251, 191, 36, 0.15); padding: 12px; border-radius: 8px;">
            <p style="margin: 0 0 8px 0;"><strong>Final Artist Values:</strong></p>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Manuel: 1st, 2nd, 1st, 2nd</span>
              <span style="color: #fbbf24; font-weight: 600;">$100k</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Sigrid: 2nd, 1st, 2nd, 1st</span>
              <span style="color: #fbbf24; font-weight: 600;">$100k</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Rafael: 3rd, 3rd (only in R3-R4)</span>
              <span>$20k</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span>Daniel: 3rd (only in R2)</span>
              <span>$10k</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="color: rgba(255,255,255,0.6);">Ramon: Never placed!</span>
              <span style="color: rgba(255,255,255,0.6);">$0</span>
            </div>
          </div>
          <div style="background: rgba(255, 152, 0, 0.15); padding: 12px; border-radius: 8px;">
            <p style="margin: 0;"><strong>Why Bob Lost:</strong></p>
            <p style="margin: 4px 0 0 0; font-size: 13px; color: rgba(255,255,255,0.8);">Bob bought Ramon paintings thinking they'd be valuable, but Ramon <strong>never placed</strong> - so all his Ramon paintings were worth $0!</p>
          </div>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 27: Key Takeaways
  // ==========================================
  {
    id: 27,
    phase: 'complete',
    gameState: TUTORIAL_GAME_COMPLETE,
    tooltip: {
      title: '🎓 Key Takeaways',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 12px;">
          <p style="margin: 0;">Remember these <strong>winning strategies</strong> for your first real game:</p>
          <div style="display: flex; flex-direction: column; gap: 8px;">
            <div style="background: rgba(251, 191, 36, 0.15); padding: 10px; border-radius: 8px; border-left: 3px solid #fbbf24;">
              <strong>1. Buy Artists Who Place Multiple Times</strong><br/>
              <span style="font-size: 13px; color: rgba(255,255,255,0.8);">Manuel & Sigrid placed in ALL 4 rounds - their paintings reached $100k!</span>
            </div>
            <div style="background: rgba(255, 152, 0, 0.15); padding: 10px; border-radius: 8px; border-left: 3px solid #ff9800;">
              <strong>2. Avoid Artists Who Might Miss</strong><br/>
              <span style="font-size: 13px; color: rgba(255,255,255,0.8);">Bob lost because Ramon never placed - all those paintings were worth $0!</span>
            </div>
            <div style="background: rgba(251, 191, 36, 0.15); padding: 10px; border-radius: 8px; border-left: 3px solid #fbbf24;">
              <strong>3. Start Small, Watch the Market</strong><br/>
              <span style="font-size: 13px; color: rgba(255,255,255,0.8);">Don't spend all your money early. See which artists are gaining popularity!</span>
            </div>
          </div>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: true,
    },
    highlights: [],
    backdropOpacity: 0.85,
  },

  // ==========================================
  // STEP 28: Tutorial Complete
  // ==========================================
  {
    id: 28,
    phase: 'complete',
    gameState: TUTORIAL_GAME_COMPLETE,
    tooltip: {
      title: '🎉 Tutorial Complete!',
      content: {
        html: `<div style="display: flex; flex-direction: column; gap: 16px; text-align: center;">
          <p style="margin: 0; font-size: 16px;">You now know how to play <strong style="color: #fbbf24;">Bougie Art</strong>!</p>
          <div style="background: rgba(76, 175, 80, 0.2); padding: 16px; border-radius: 12px; border: 2px solid #4CAF50;">
            <div style="font-size: 24px; margin-bottom: 8px;">🚀</div>
            <p style="margin: 0; font-weight: 600;">Ready to Play?</p>
            <p style="margin: 8px 0 0 0; font-size: 13px; color: rgba(255,255,255,0.7);">
              Click Exit or press Escape to start your first game!
            </p>
          </div>
        </div>`
      },
      position: 'center',
      targetElement: null,
      showControls: false,
    },
    highlights: [],
    backdropOpacity: 0.9,
  },
]

// Phase checkpoints for skip navigation (future use)
export const PHASE_CHECKPOINTS: Record<TutorialPhase, number> = {
  intro: 1,              // Steps 1-6: Welcome, artists, hand, rounds, opponents, auction area
  card_selection: 7,     // Step 7: Your first auction
  auction_basics: 8,     // Steps 8-9: Hidden auction + result
  auction_types: 10,     // Steps 10-15: 5 auction types overview + details
  round_ending: 16,      // Step 16: How rounds end
  valuation: 17,         // Steps 17, 19-22, 24: Valuation steps
  scoring: 18,           // Steps 18, 21, 23, 25: Round complete steps
  complete: 28,          // Step 28: Tutorial complete
}
